import React, { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { Table, Tag, Button, Input, Space, Tooltip, Switch, Popconfirm } from "antd";
import {
  EyeOutlined,
  PhoneOutlined,
  MailOutlined,
  UserOutlined,
} from "@ant-design/icons";
import { all_routes } from "../../../router/all_routes";
import { showToast } from "../../../../core/data/redux/slices/ToastSlice";
import api from "../../../../core/axios/axiosInstance";
import { useDebounce } from "../../../../core/hooks/useDebounce";
import {
  setCompaniesPage,
  getCompaniesPage,
  clearCompaniesCache,
  setSearchQuery,
  setCurrentPage,
  setLoading,
  setError,
} from "../../../../core/data/redux/slices/adminCompanySlice";
import { MdDeleteForever } from "react-icons/md";

const ManageSuspendedCompanies = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  // Get data from Redux
  const { companies, cachedPages, pagination, searchQuery, loading } =
    useSelector((state) => state.adminCompany);

  const suspendedCompanies = companies?.filter(
    (company) => company.accountStatus === 'suspended'
  );


  const [localSearch, setLocalSearch] = useState(searchQuery);
  const debouncedSearch = useDebounce(localSearch, 500);
  const [loadingToggles, setLoadingToggles] = useState({});
  const [recordToDelete, setRecordToDelete] = useState({});
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [bulkDeleting, setBulkDeleting] = useState(false);



  useEffect(()=>{
    return (()=>{
      setLocalSearch("")
                              dispatch(setSearchQuery(""));
                              dispatch(setCurrentPage(1));
                              dispatch(clearCompaniesCache());
    })
  },[])
  useEffect(() => {
    fetchCompanyAdmins(pagination.current, pagination.pageSize, searchQuery);
    // eslint-disable-next-line
  }, [pagination.current, pagination.pageSize, searchQuery]);

  const fetchCompanyAdmins = async (
    page = 1,
    limit = 10,
    search = "",
    forceRefresh = false
  ) => {
    // Check if page is already cached
    if (cachedPages[page] && search === searchQuery && !forceRefresh) {
      dispatch(getCompaniesPage(page));
      return;
    }
    dispatch(setLoading(true));
    try {
      const response = await api.post("/superAdmin/allCompanyAdmin", {
        page,
        limit,
        search,
      });
      if (response.data.status === "success") {
        dispatch(
          setCompaniesPage({
            page,
            data: response.data.data,
            pagination: {
              current: response.data.page,
              pageSize: response.data.limit,
              total: response.data.totalAdmins,
            },
          })
        );
      }
    } catch (error) {
      dispatch(setError(error.message));
      dispatch(
        showToast({
          heading: "Error",
          message: "Failed to fetch company admins",
          variant: "danger",
        })
      );
    } finally {
      dispatch(setLoading(false));
    }
  };


  const rowSelection = {
    selectedRowKeys,
    onChange: (keys) => setSelectedRowKeys(keys),
  };

  const handleBulkDelete = async () => {
    setBulkDeleting(true);
    try {
      await api.delete("/deleteUser/bulk", {
        data: { companyIds: selectedRowKeys },
      });
      dispatch(showToast({ message: `${selectedRowKeys.length} companies deactivated`, variant: "success" }));
      setSelectedRowKeys([]);
      dispatch(clearCompaniesCache());
      fetchCompanyAdmins(1, pagination.pageSize, searchQuery);
    } catch (err) {
      dispatch(showToast({ message: err?.response?.data?.message || "Bulk delete failed", variant: "danger" }));
    } finally {
      setBulkDeleting(false);
    }
  };

  const handleDeleteCompany = async (record) => {
    try {
      const payload = {
        user_id: record._id
      }

      const response = await api.delete("/deleteAllUserData", {
        data: payload,
      });

      await fetchCompanyAdmins(
        pagination.current,
        pagination.pageSize,
        searchQuery,
        true
      );

    } catch (error) {

    }
  }

  const handleTableChange = (pag) => {
    dispatch(setCurrentPage(pag.current));
  };

  // handleSearch is kept for manual triggering if needed (not used by default)
  const handleSearch = (value) => {
    dispatch(setSearchQuery(value));
    dispatch(clearCompaniesCache());
    fetchCompanyAdmins(1, pagination.pageSize, value);
  };

  // When the debounced input changes, update the global search query and clear cache.
  useEffect(() => {
    // Avoid dispatching if nothing changed
    if (debouncedSearch !== searchQuery) {
      dispatch(setSearchQuery(debouncedSearch));
      dispatch(clearCompaniesCache());
      // fetching is handled by the existing useEffect that listens to searchQuery
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedSearch]);

  const handleViewDetails = (admin) => {
    navigate(all_routes.companyDetails, { state: { company: admin } });
  };

  const handleToggleAccountStatus = async (userId, currentStatus) => {
    const toggleKey = `account-${userId}`;
    setLoadingToggles((prev) => ({ ...prev, [toggleKey]: true }));

    try {
      const newStatus = currentStatus === "active" ? "suspended" : "active";
      const endpoint =
        newStatus === "active" ? "/deleteUser/activate" : "/deleteUser/suspend";

      const response = await api.post(endpoint, {
        user_id: userId,
      });

      if (response.data.status === "success") {
        // Allow animation to complete before refreshing
        await new Promise((resolve) => setTimeout(resolve, 800));

        dispatch(
          showToast({
            heading: "Success",
            message: response.data.message,
            variant: "success",
          })
        );

        // dispatch(clearCompaniesCache());
        await fetchCompanyAdmins(
          pagination.current,
          pagination.pageSize,
          searchQuery,
          true
        );
      }
    } catch (error) {
      const msg =
        error?.response?.data?.message || "Failed to update account status";
      dispatch(
        showToast({
          heading: "Error",
          message: msg,
          variant: "danger",
        })
      );
    } finally {
      setLoadingToggles((prev) => {
        const newState = { ...prev };
        delete newState[toggleKey];
        return newState;
      });
    }
  };

  const handleToggleExtensionStatus = async (userId, company, checked) => {
    const toggleKey = `extension-${userId}`;
    setLoadingToggles((prev) => ({ ...prev, [toggleKey]: true }));

    try {
      const response = await api.put("/superAdmin/editCompanyAdminAndAgent", {
        userId: userId,
        extensionNumber: company.extensionNumber,
        telephone: company.telephone,
        sipSecret: company.sipSecret,
        status: checked,
      });

      if (response.data.status === "success") {
        // Allow animation to complete before refreshing
        await new Promise((resolve) => setTimeout(resolve, 800));

        dispatch(
          showToast({
            heading: "Success",
            message: checked
              ? "Extension activated successfully"
              : "Extension deactivated successfully",
            variant: "success",
          })
        );

        // dispatch(clearCompaniesCache());
        await fetchCompanyAdmins(
          pagination.current,
          pagination.pageSize,
          searchQuery,
          true
        );
      }
    } catch (error) {
      const msg =
        error?.response?.data?.error || "Failed to update extension status";
      dispatch(
        showToast({
          heading: "Error",
          message: msg,
          variant: "danger",
        })
      );
    } finally {
      setLoadingToggles((prev) => {
        const newState = { ...prev };
        delete newState[toggleKey];
        return newState;
      });
    }
  };

  const adminColumns = [
    {
      title: "Name",
      key: "name",
      render: (_, record) => (
        <span>
          <UserOutlined style={{ marginRight: 8 }} />
          {record.firstname} {record.lastname}
        </span>
      ),
    },
    {
      title: "Email",
      dataIndex: "email",
      key: "email",
      render: (email) => (
        <span>
          <MailOutlined style={{ marginRight: 8 }} />
          {email}
        </span>
      ),
    },
    {
      title: "Company Name",
      dataIndex: "userInfo",
      key: "companyName",
      render: (userInfo) => <span>{userInfo?.companyName || "N/A"}</span>,
    },
    {
      title: "Extension Status",
      dataIndex: "extensionStatus",
      key: "extensionStatus",
      render: (extensionStatus, record) => {
        const toggleKey = `extension-${record._id}`;
        return (
          <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
            <Switch
              checked={extensionStatus}
              loading={loadingToggles[toggleKey]}
              onChange={(checked) =>
                handleToggleExtensionStatus(record._id, record, checked)
              }
              checkedChildren={"Active"}
              unCheckedChildren={"Inactive"}
              size="large"
            />
          </div>
        );
      },
    },
    {
      title: "Account Status",
      dataIndex: "accountStatus",
      key: "accountStatus",
      render: (accountStatus, record) => {
        const toggleKey = `account-${record._id}`;
        return (
          <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
            <Switch
              checked={accountStatus === "active"}
              loading={loadingToggles[toggleKey]}
              onChange={() =>
                handleToggleAccountStatus(record._id, accountStatus)
              }
              checkedChildren={"Active"}
              unCheckedChildren={"Suspended"}
              size="large"
            />
          </div>
        );
      },
    },
    {
      title: "Extension",
      dataIndex: "extensionNumber",
      key: "extensionNumber",
      render: (ext) => <Tag color="blue">{ext || "N/A"}</Tag>,
    },
    {
      title: "Telephone Number",
      dataIndex: "telephone",
      key: "telephone",
      render: (phone) => (
        <span>
          <PhoneOutlined style={{ marginRight: 8 }} />
          {phone || "N/A"}
        </span>
      ),
    },
    {
      title: "Created",
      dataIndex: "createdAt",
      key: "createdAt",
      render: (date) => new Date(date).toLocaleDateString(),
    },
    {
      title: "Actions",
      key: "actions",
      render: (_, record) => (
        <div >
          <Space className="me-2">
            <Tooltip title="View Details">
              <Button
                type="primary"
                icon={<EyeOutlined />}
                size="small"
                onClick={() => handleViewDetails(record)}
              >
                View
              </Button>
            </Tooltip>
          </Space>
          <Space>
            <Tooltip title="Delete Company">
              <Button
                danger
                icon={<MdDeleteForever />}
                size="small"
                onClick={() => setRecordToDelete(record)}
                data-bs-toggle="modal"
                data-bs-target="#deleteModal"
              >
                Delete
                {/* <Link
                              className="dropdown-item"
                              to="#"
                              data-bs-toggle="modal"
                              data-bs-target="#deleteModal"
                            >
                              Delete
                            </Link> */}
              </Button>
            </Tooltip>
          </Space>
        </div>
      ),
    },
  ];

  return (
    <div className="page-wrapper">
      <div className="content">
        <div className="row">
          <div className="col-md-12">
            <div className="page-header">
              <div className="row">
                <div className="col-sm-12">
                  <div className="d-flex justify-content-between align-items-center">
                    <div>
                      <h3 className="page-title mt-2 mt-md-0">Manage Suspended Companies</h3>
                      <ul className="breadcrumb">
                        <li className="breadcrumb-item">
                          <Link to={all_routes.adminDashboard}>Dashboard</Link>
                        </li>
                        <li className="breadcrumb-item active">Suspended Companies</li>
                      </ul>
                    </div>
                    {/* <Button
                      danger
                      icon={<MdDeleteForever />}
                      onClick={() => { }}
                    >
                      Suspended Companies
                    </Button> */}

                  </div>
                </div>
              </div>
            </div>
            <div className="card">
              <div className="card-body table-responsive custom-table">
                <div className="d-flex flex-column flex-sm-row justify-content-between  align-items-start align-items-sm-center mb-3">
                  <h5 className="fw-semibold mb-2 mt-2 ">All Suspended Company Admins</h5>
                  <div className="d-flex gap-2 align-items-center" style={{ maxWidth: "500px", width: "100%" }}>
                    {selectedRowKeys.length > 0 && (
                      <Popconfirm
                        title={`Deactivate ${selectedRowKeys.length} selected suspended companies?`}
                        onConfirm={handleBulkDelete}
                        okText="Deactivate"
                        okButtonProps={{ danger: true }}
                      >
                        <Button danger loading={bulkDeleting} icon={<MdDeleteForever />}>
                          Deactivate Selected ({selectedRowKeys.length})
                        </Button>
                      </Popconfirm>
                    )}
                    <Input
                      placeholder="Search by name, email, phone..."
                      value={localSearch}
                      onChange={(e) => setLocalSearch(e.target.value)}
                      style={{ width: "100%" }}
                      allowClear
                    />
                  </div>
                </div>
                <Table
                  columns={adminColumns}
                  dataSource={suspendedCompanies}
                  rowKey="_id"
                  loading={loading}
                  rowSelection={rowSelection}
                  pagination={{
                    current: pagination.current,
                    pageSize: pagination.pageSize,
                    total: pagination.total,
                    showSizeChanger: true,
                  }}
                  onChange={handleTableChange}
                  bordered
                />
              </div>
            </div>
          </div>
        </div>
      </div>





      <div
        className="modal fade"
        id="deleteModal"
        tabIndex="-1"
        aria-labelledby="logout"
        role="dialog"
      >
        <div className="modal-dialog modal-dialog-centered">
          <div className="modal-content">
            <div className="modal-body">
              <div className="text-center">
                <div className="avatar avatar-xl bg-theme-light rounded-circle mb-3">
                  <i className="fa-solid fa-right-from-bracket fs-36 text-theme" />
                </div>
                <h4 className="mb-2 text-capitalize">Delete?</h4>
                <p className="mb-0">
                  Are you sure you want to delete the company permanently?
                </p>
                <div className="d-flex align-items-center justify-content-center mt-4">
                  <Link
                    to="#"
                    className="btn btn-light me-2"
                    data-bs-dismiss="modal"
                  >
                    Cancel
                  </Link>
                  <Link
                    to={"#"}
                    data-bs-dismiss="modal"
                    className="btn btn-theme text-white"
                    onClick={() => handleDeleteCompany(recordToDelete)}

                  >
                    Yes, Delete
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>







    </div>
  );
};

export default ManageSuspendedCompanies;
